// Parse common South African bank CSV statement exports into a normalised shape:
//   [{ date: "YYYY-MM-DD", description, amount, balance, raw }]
// Positive amount = money in, negative = money out.
// Supports FNB, Standard Bank, ABSA, Capitec, Nedbank header conventions, plus a generic fallback.

function splitCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseDate(s) {
  if (!s) return null;
  const t = String(s).trim();
  // ISO already — dash, slash, or dot separators (e.g. "2026-06-13", "2026/06/13").
  let m = t.match(/^(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})/);
  if (m) return pad(m[1], 4) + '-' + pad(m[2], 2) + '-' + pad(m[3], 2);
  // Contiguous 8-digit dates, no separators (e.g. ABSA "20260613").
  // YYYYMMDD if it starts with a 19xx/20xx year; otherwise assume DDMMYYYY.
  m = t.match(/^(\d{8})$/);
  if (m) {
    const d = m[1];
    if (/^(19|20)/.test(d)) return d.slice(0, 4) + '-' + d.slice(4, 6) + '-' + d.slice(6, 8);
    return d.slice(4, 8) + '-' + d.slice(2, 4) + '-' + d.slice(0, 2);
  }
  // DD/MM/YYYY or DD-MM-YYYY
  m = t.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/);
  if (m) {
    let y = m[3];
    if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y;
    return y + '-' + pad(m[2], 2) + '-' + pad(m[1], 2);
  }
  // DD MMM YYYY (e.g. "15 Mar 2025")
  m = t.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{2,4})/);
  if (m) {
    const months = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
    const mi = months.indexOf(m[2].slice(0, 3).toLowerCase());
    if (mi >= 0) {
      let y = m[3];
      if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y;
      return y + '-' + pad(String(mi + 1), 2) + '-' + pad(m[1], 2);
    }
  }
  return null;
}

function pad(s, n) {
  s = String(s);
  while (s.length < n) s = '0' + s;
  return s;
}

function parseAmount(s) {
  if (s == null || s === '') return 0;
  let t = String(s).trim();
  if (!t) return 0;
  // South African format may use comma as decimal or thousand separator.
  // Strip currency symbols and spaces.
  t = t.replace(/[Rr$\s]/g, '');
  // Detect "1,234.56" (comma thousands, dot decimal) vs "1.234,56" (dot thousands, comma decimal).
  const hasComma = t.indexOf(',') >= 0;
  const hasDot = t.indexOf('.') >= 0;
  if (hasComma && hasDot) {
    // Whichever appears last is the decimal mark.
    if (t.lastIndexOf(',') > t.lastIndexOf('.')) {
      t = t.replace(/\./g, '').replace(',', '.');
    } else {
      t = t.replace(/,/g, '');
    }
  } else if (hasComma) {
    // Comma only — assume decimal if it looks like ",dd" at end
    if (/,\d{1,2}$/.test(t)) t = t.replace(',', '.');
    else t = t.replace(/,/g, '');
  }
  // Handle parens for negatives, e.g. (123.45)
  let negative = false;
  if (/^\(.+\)$/.test(t)) { negative = true; t = t.slice(1, -1); }
  const n = parseFloat(t);
  if (isNaN(n)) return 0;
  return negative ? -n : n;
}

function pickHeader(headers, candidates) {
  for (const c of candidates) {
    const i = headers.findIndex((h) => h.toLowerCase().replace(/[^a-z0-9]/g, '') === c.toLowerCase().replace(/[^a-z0-9]/g, ''));
    if (i >= 0) return i;
  }
  for (const c of candidates) {
    const i = headers.findIndex((h) => h.toLowerCase().indexOf(c.toLowerCase()) >= 0);
    if (i >= 0) return i;
  }
  return -1;
}

// Best-effort: find the header row by scanning the first ~20 lines for one that contains a date column.
function findHeaderRow(lines) {
  for (let i = 0; i < Math.min(lines.length, 25); i++) {
    const cells = splitCsvLine(lines[i]);
    const lc = cells.map((c) => c.toLowerCase());
    const hasDate = lc.some((c) => /date/.test(c));
    const hasAmtOrBal = lc.some((c) => /amount|debit|credit|balance|in\s*\(|out\s*\(/.test(c));
    if (hasDate && hasAmtOrBal && cells.length >= 3) return i;
  }
  return -1;
}

export function parseBankCsv(text) {
  const allLines = text.split(/\r?\n/).filter((l) => l.length > 0);
  const headerIdx = findHeaderRow(allLines);
  if (headerIdx < 0) {
    return { rows: [], headers: [], error: 'Could not locate a header row containing Date and Amount/Balance columns.' };
  }
  const headers = splitCsvLine(allLines[headerIdx]);
  const dataLines = allLines.slice(headerIdx + 1);

  const dateCol = pickHeader(headers, ['Date', 'Transaction Date', 'Posting Date', 'Value Date']);
  const descCol = pickHeader(headers, ['Description', 'Narrative', 'Details', 'Reference', 'Transaction', 'Memo']);
  const amountCol = pickHeader(headers, ['Amount', 'Transaction Amount', 'Value']);
  const debitCol = pickHeader(headers, ['Debit', 'Money Out', 'Withdrawal', 'Out', 'Debits']);
  const creditCol = pickHeader(headers, ['Credit', 'Money In', 'Deposit', 'In', 'Credits']);
  const balanceCol = pickHeader(headers, ['Balance', 'Running Balance', 'Closing Balance']);

  const rows = [];
  let dataRowCount = 0;    // lines that look like real data (enough cells)
  let dateParseFails = 0;  // of those, how many had an unparseable date
  let sampleBadDate = '';  // first offending raw date cell, for the error message
  for (const line of dataLines) {
    const cells = splitCsvLine(line);
    if (cells.length < 2) continue;
    dataRowCount++;
    const date = dateCol >= 0 ? parseDate(cells[dateCol]) : null;
    if (!date) {
      dateParseFails++;
      if (!sampleBadDate && dateCol >= 0 && cells[dateCol]) sampleBadDate = cells[dateCol].trim();
      continue;
    }
    const description = descCol >= 0 ? cells[descCol] : cells.slice(1, -1).join(' ');
    let amount = 0;
    if (amountCol >= 0) {
      amount = parseAmount(cells[amountCol]);
    } else {
      const dr = debitCol >= 0 ? parseAmount(cells[debitCol]) : 0;
      const cr = creditCol >= 0 ? parseAmount(cells[creditCol]) : 0;
      // Bank exports often render debits as positive in their own column — flip sign so positive = money in.
      amount = cr - Math.abs(dr);
    }
    const balance = balanceCol >= 0 ? parseAmount(cells[balanceCol]) : null;
    rows.push({ date, description: (description || '').trim(), amount, balance, raw: cells });
  }
  // Distinguish a genuinely empty statement from an unrecognised date format:
  // if every data row was dropped purely because its date wouldn't parse, say so
  // loudly (with a sample) instead of silently reporting "no transactions".
  if (!rows.length && dataRowCount > 0 && dateParseFails === dataRowCount) {
    return {
      rows: [], headers, headerIndex: headerIdx,
      error: 'Found ' + dataRowCount + ' data row' + (dataRowCount === 1 ? '' : 's')
        + " but couldn't read the date format" + (sampleBadDate ? ' (e.g. "' + sampleBadDate + '")' : '')
        + '. Supported: YYYY-MM-DD, YYYY/MM/DD, DD/MM/YYYY, DD MMM YYYY, YYYYMMDD.',
    };
  }
  return { rows, headers, headerIndex: headerIdx };
}

// Parse the first non-empty sheet of an xlsx ArrayBuffer using SheetJS, then convert to CSV
// internally and reuse parseBankCsv so all the column-sniffing logic stays in one place.
export function parseBankXlsx(arrayBuffer, XLSX) {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  const csv = XLSX.utils.sheet_to_csv(sheet, { FS: ',' });
  return parseBankCsv(csv);
}
